/**
 * Every server interaction in the app, as a hook.
 *
 * Screens do not call `api` directly — they call these. That keeps query keys
 * consistent (so invalidation actually invalidates), keeps stale times in one
 * table, and means a change to an endpoint is fixed once.
 */
import { useCallback } from 'react'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { API_BASE_URL, api, fetchSession, login, logout, unwrap, type SessionResponse } from './api'
import type { paths } from './api-types'

/* ------------------------------------------------------------------ *
 * Freshness policy
 *
 * Balances, cycles and liabilities are refreshed by the backend on a
 * schedule and by Plaid webhooks — the browser polling them harder does
 * not make them newer, it just burns requests. So the defaults lean long,
 * and the things a user changes themselves are invalidated explicitly.
 * ------------------------------------------------------------------ */
export const STALE = {
  /** Things the user is actively editing, or that a job may have just written. */
  live: 15_000,
  /** Alerts, AI budget, sync status. */
  short: 60_000,
  /** Balances, dashboards, statement cycles — backend-refreshed. */
  balances: 5 * 60_000,
  /** Reports over closed periods. */
  reports: 10 * 60_000,
  /** Categories, members, settings, securities — reference data. */
  reference: 30 * 60_000,
} as const

/* ------------------------------------------------------------------ *
 * Query keys
 *
 * One factory, hierarchical, so `queryClient.invalidateQueries({ queryKey:
 * qk.accounts.all })` reaches every account query including details.
 * ------------------------------------------------------------------ */
export const qk = {
  session: ['session'] as const,
  dashboard: ['dashboard'] as const,

  accounts: {
    all: ['accounts'] as const,
    list: (query?: AccountsQuery) => ['accounts', 'list', query ?? {}] as const,
    detail: (id: string) => ['accounts', 'detail', id] as const,
    balances: (id: string, query?: BalancesQuery) => ['accounts', id, 'balances', query ?? {}] as const,
    cycles: (id: string, take?: number) => ['accounts', id, 'cycles', take ?? null] as const,
    promoAprs: (id: string) => ['accounts', id, 'promo-aprs'] as const,
  },

  transactions: {
    all: ['transactions'] as const,
    list: (query?: TransactionsQuery) => ['transactions', 'list', query ?? {}] as const,
    detail: (id: string) => ['transactions', 'detail', id] as const,
    subscriptions: (id: string) => ['transactions', id, 'subscriptions'] as const,
  },

  categories: {
    all: ['categories'] as const,
    list: ['categories', 'list'] as const,
    tree: ['categories', 'tree'] as const,
    detail: (id: string) => ['categories', 'detail', id] as const,
  },

  rules: {
    all: ['rules'] as const,
    list: (query?: RulesQuery) => ['rules', 'list', query ?? {}] as const,
    detail: (id: string) => ['rules', 'detail', id] as const,
  },

  members: {
    all: ['members'] as const,
    list: ['members', 'list'] as const,
    detail: (id: string) => ['members', 'detail', id] as const,
  },

  income: {
    all: ['income'] as const,
    sources: (query?: IncomeSourcesQuery) => ['income', 'sources', query ?? {}] as const,
    source: (id: string) => ['income', 'sources', 'detail', id] as const,
    events: (query?: IncomeEventsQuery) => ['income', 'events', query ?? {}] as const,
    event: (id: string) => ['income', 'events', 'detail', id] as const,
  },

  subscriptions: {
    all: ['subscriptions'] as const,
    list: (query?: SubscriptionsQuery) => ['subscriptions', 'list', query ?? {}] as const,
    detail: (id: string) => ['subscriptions', 'detail', id] as const,
    totals: ['subscriptions', 'totals'] as const,
    allocationGaps: (take?: number) => ['subscriptions', 'allocation-gaps', take ?? null] as const,
    charges: (id: string, take?: number) => ['subscriptions', id, 'charges', take ?? null] as const,
  },

  budgetRules: {
    all: ['budget-rules'] as const,
    list: (includeInactive?: boolean) => ['budget-rules', 'list', includeInactive ?? false] as const,
    detail: (id: string) => ['budget-rules', 'detail', id] as const,
  },

  dueDateRules: {
    all: ['due-date-rules'] as const,
    list: ['due-date-rules', 'list'] as const,
    detail: (id: string) => ['due-date-rules', 'detail', id] as const,
  },

  alerts: {
    all: ['alerts'] as const,
    list: (query?: AlertsQuery) => ['alerts', 'list', query ?? {}] as const,
    detail: (id: string) => ['alerts', 'detail', id] as const,
    deliveries: (id: string) => ['alerts', id, 'deliveries'] as const,
  },

  analytics: {
    all: ['analytics'] as const,
    spending: (query?: SpendingQuery) => ['analytics', 'spending', query ?? {}] as const,
    income: (query?: AnalyticsIncomeQuery) => ['analytics', 'income', query ?? {}] as const,
    netWorth: (query?: NetWorthQuery) => ['analytics', 'net-worth', query ?? {}] as const,
    cashflow: (months?: number) => ['analytics', 'cashflow', months ?? null] as const,
    subscriptions: ['analytics', 'subscriptions'] as const,
  },

  investments: {
    all: ['investments'] as const,
    holdings: (query?: PortfolioScopeQuery) => ['investments', 'holdings', query ?? {}] as const,
    securities: ['investments', 'securities'] as const,
    transactions: (query?: InvestmentTransactionsQuery) =>
      ['investments', 'transactions', query ?? {}] as const,
    allocation: (query?: PortfolioScopeQuery) => ['investments', 'allocation', query ?? {}] as const,
    performance: (query?: PortfolioPerformanceQuery) =>
      ['investments', 'performance', query ?? {}] as const,
    valueSeries: (query?: PortfolioValueSeriesQuery) =>
      ['investments', 'value-series', query ?? {}] as const,
    movers: (query?: PortfolioMoversQuery) => ['investments', 'movers', query ?? {}] as const,
    concentration: (query?: PortfolioConcentrationQuery) =>
      ['investments', 'concentration', query ?? {}] as const,
  },

  debt: {
    all: ['debt'] as const,
    summary: ['debt', 'summary'] as const,
    payoff: (accountId: string) => ['debt', 'payoff', accountId] as const,
    amortization: (accountId: string, extraMonthlyPayment = 0) =>
      ['debt', 'amortization', accountId, extraMonthlyPayment] as const,
    strategy: (monthlyBudget: number | null) => ['debt', 'strategy', monthlyBudget] as const,
    promoCost: ['debt', 'promo-cost'] as const,
  },

  ai: {
    all: ['ai'] as const,
    status: ['ai', 'status'] as const,
    conversations: ['ai', 'conversations'] as const,
    conversation: (id: string) => ['ai', 'conversations', id] as const,
    insights: (query?: InsightsQuery) => ['ai', 'insights', query ?? {}] as const,
    reviewQueue: (take?: number) => ['ai', 'review-queue', take ?? null] as const,
  },

  settings: {
    all: ['settings'] as const,
    detail: ['settings', 'detail'] as const,
    recipients: ['settings', 'recipients'] as const,
  },

  transactionWatches: {
    all: ['transaction-watches'] as const,
    list: ['transaction-watches', 'list'] as const,
    detail: (id: string) => ['transaction-watches', 'detail', id] as const,
  },

  plaid: {
    all: ['plaid'] as const,
    items: ['plaid', 'items'] as const,
  },
} as const

/* ------------------------------------------------------------------ *
 * Query/body types, pulled straight off the generated contract.
 * ------------------------------------------------------------------ */
type Query<P extends keyof paths, M extends keyof paths[P]> = paths[P][M] extends {
  parameters: { query?: infer Q }
}
  ? NonNullable<Q>
  : never

type Body<P extends keyof paths, M extends keyof paths[P]> = paths[P][M] extends {
  requestBody?: { content: { 'application/json': infer B } }
}
  ? NonNullable<B>
  : never

export type AccountsQuery = Query<'/api/accounts', 'get'>
export type BalancesQuery = Query<'/api/accounts/{id}/balances', 'get'>
export type TransactionsQuery = Query<'/api/transactions', 'get'>
export type RulesQuery = Query<'/api/rules', 'get'>
export type IncomeSourcesQuery = Query<'/api/income-sources', 'get'>
export type IncomeEventsQuery = Query<'/api/income-events', 'get'>
export type SubscriptionsQuery = Query<'/api/subscriptions', 'get'>
export type AlertsQuery = Query<'/api/alerts', 'get'>
export type SpendingQuery = Query<'/api/analytics/spending', 'get'>
export type AnalyticsIncomeQuery = Query<'/api/analytics/income', 'get'>
export type NetWorthQuery = Query<'/api/analytics/net-worth', 'get'>
export type InvestmentTransactionsQuery = Query<'/api/investments/transactions', 'get'>
/** `accountId` and `memberId` — the household/individual lens (D28), plus one-account drilldown. */
export type PortfolioScopeQuery = Query<'/api/investments/holdings', 'get'>
export type PortfolioPerformanceQuery = Query<'/api/investments/performance', 'get'>
export type PortfolioValueSeriesQuery = Query<'/api/investments/value-series', 'get'>
export type PortfolioMoversQuery = Query<'/api/investments/movers', 'get'>
export type PortfolioConcentrationQuery = Query<'/api/investments/concentration', 'get'>
export type InsightsQuery = Query<'/api/ai/insights', 'get'>

export type UpdateAccountBody = Body<'/api/accounts/{id}', 'put'>
export type UpdateTransactionBody = Body<'/api/transactions/{id}', 'patch'>
export type BulkCategorizeBody = Body<'/api/transactions/bulk-categorize', 'post'>
export type ReclassifyBody = Body<'/api/transactions/reclassify', 'post'>
export type LinkSubscriptionBody = Body<'/api/transactions/{id}/subscriptions', 'post'>
export type CategoryBody = Body<'/api/categories', 'post'>
export type RuleBody = Body<'/api/rules', 'post'>
export type MemberBody = Body<'/api/members', 'post'>
export type IncomeSourceBody = Body<'/api/income-sources', 'post'>
export type IncomeEventBody = Body<'/api/income-events', 'post'>
export type SubscriptionBody = Body<'/api/subscriptions', 'post'>
export type SubscriptionLinkBody = Body<'/api/subscriptions/{id}/transactions', 'post'>
export type BudgetRuleBody = Body<'/api/budget-rules', 'post'>
export type DueDateRuleBody = Body<'/api/due-date-rules', 'post'>
export type PromoAprBody = Body<'/api/accounts/{id}/promo-aprs', 'post'>
export type SettingsBody = Body<'/api/settings', 'put'>
export type RecipientBody = Body<'/api/settings/recipients', 'post'>
export type TransactionWatchBody = Body<'/api/transaction-watches', 'post'>
export type ApproveCategoryBody = Body<'/api/ai/review-queue/approve', 'post'>
export type ExchangeBody = Body<'/api/plaid/exchange', 'post'>

/** Extra options a caller may pass to any query hook (`enabled`, `select`, …). */
type Options<TData> = Omit<UseQueryOptions<TData, Error, TData>, 'queryKey' | 'queryFn'>

/** Extra options a caller may pass to any mutation hook (`onSuccess`, …). */
type MutationOptions<TData, TVars> = Omit<
  UseMutationOptions<TData, Error, TVars>,
  'mutationFn'
>

/* ================================================================== *
 * Session
 * ================================================================== */

/**
 * The auth guard's source of truth. `/api/auth/me` answers 200 with
 * `authenticated: false` for anonymous callers, so this never throws on a
 * signed-out visitor — an error here means the server is actually unreachable.
 */
export function useSession(options?: Options<SessionResponse>) {
  return useQuery({
    queryKey: qk.session,
    queryFn: ({ signal }) => fetchSession(signal),
    staleTime: STALE.short,
    retry: false,
    ...options,
  })
}

export interface Credentials {
  username: string
  password: string
}

export function useLogin(options?: MutationOptions<SessionResponse, Credentials>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ username, password }: Credentials) => login(username, password),
    ...options,
    onSuccess: (...args) => {
      queryClient.setQueryData(qk.session, args[0])
      void queryClient.invalidateQueries()
      options?.onSuccess?.(...args)
    },
  })
}

export function useLogout(options?: MutationOptions<void, void>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => logout(),
    ...options,
    onSuccess: (...args) => {
      queryClient.clear()
      options?.onSuccess?.(...args)
    },
  })
}

/* ================================================================== *
 * Dashboard
 * ================================================================== */

export function useDashboard(
  memberId?: string | null,
  includeTransfers = false,
  options?: Options<SchemaOf<'/api/dashboard'>>,
) {
  return useQuery({
    // Both lenses are part of the key: switching person must refetch, not reuse the
    // household's figures under a personal label, and a transfer-inclusive total is a
    // different question rather than a different rendering of the same answer.
    queryKey: [...qk.dashboard, memberId ?? 'household', includeTransfers ? 'transfers' : 'strict'],
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/dashboard', {
          params: {
            query: {
              ...(memberId ? { memberId } : {}),
              ...(includeTransfers ? { includeTransfers: true } : {}),
            },
          },
          signal,
        } as never),
      ),
    staleTime: STALE.balances,

    // Flipping a toggle must not blank the screen it sits on. The previous answer
    // stays up while the new one loads, and the payload's own `includesTransfers`
    // is what the tile describes, so nothing is ever labeled as the wrong question.
    placeholderData: keepPreviousData,
    ...options,
  })
}

/** Response type of a GET path — saves spelling out `components['schemas'][…]`. */
type SchemaOf<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer R } } } }
}
  ? R
  : never

/* ================================================================== *
 * Accounts
 * ================================================================== */

export function useAccounts(
  query?: AccountsQuery,
  options?: Options<SchemaOf<'/api/accounts'>>,
) {
  return useQuery({
    queryKey: qk.accounts.list(query),
    queryFn: ({ signal }) => unwrap(api.GET('/api/accounts', { params: { query }, signal })),
    staleTime: STALE.balances,
    ...options,
  })
}

export function useAccount(id: string, options?: Options<SchemaOf<'/api/accounts/{id}'>>) {
  return useQuery({
    queryKey: qk.accounts.detail(id),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/accounts/{id}', { params: { path: { id } }, signal })),
    staleTime: STALE.balances,
    enabled: Boolean(id),
    ...options,
  })
}

export function useAccountBalances(
  id: string,
  query?: BalancesQuery,
  options?: Options<SchemaOf<'/api/accounts/{id}/balances'>>,
) {
  return useQuery({
    queryKey: qk.accounts.balances(id, query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/accounts/{id}/balances', { params: { path: { id }, query }, signal })),
    staleTime: STALE.balances,
    enabled: Boolean(id),
    ...options,
  })
}

export function useAccountCycles(
  id: string,
  take?: number,
  options?: Options<SchemaOf<'/api/accounts/{id}/cycles'>>,
) {
  return useQuery({
    queryKey: qk.accounts.cycles(id, take),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/accounts/{id}/cycles', { params: { path: { id }, query: { take } }, signal }),
      ),
    staleTime: STALE.balances,
    enabled: Boolean(id),
    ...options,
  })
}

export function usePromoAprs(id: string, options?: Options<SchemaOf<'/api/accounts/{id}/promo-aprs'>>) {
  return useQuery({
    queryKey: qk.accounts.promoAprs(id),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/accounts/{id}/promo-aprs', { params: { path: { id } }, signal })),
    staleTime: STALE.reference,
    enabled: Boolean(id),
    ...options,
  })
}

export function useUpdateAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAccountBody }) =>
      unwrap(api.PUT('/api/accounts/{id}', { params: { path: { id } }, body })),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: qk.accounts.all })
      void queryClient.invalidateQueries({ queryKey: qk.accounts.detail(id) })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard })
      void queryClient.invalidateQueries({ queryKey: qk.debt.all })
    },
  })
}

export function useCreatePromoApr() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PromoAprBody }) =>
      unwrap(api.POST('/api/accounts/{id}/promo-aprs', { params: { path: { id } }, body })),
    onSuccess: (_data, { id }) => invalidateAccount(queryClient, id),
  })
}

export function useUpdatePromoApr() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, promoId, body }: { id: string; promoId: string; body: PromoAprBody }) =>
      unwrap(
        api.PUT('/api/accounts/{id}/promo-aprs/{promoId}', {
          params: { path: { id, promoId } },
          body,
        }),
      ),
    onSuccess: (_data, { id }) => invalidateAccount(queryClient, id),
  })
}

export function useDeletePromoApr() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, promoId }: { id: string; promoId: string }) =>
      unwrap(
        api.DELETE('/api/accounts/{id}/promo-aprs/{promoId}', {
          params: { path: { id, promoId } },
        }),
      ),
    onSuccess: (_data, { id }) => invalidateAccount(queryClient, id),
  })
}

function invalidateAccount(queryClient: QueryClient, id: string) {
  void queryClient.invalidateQueries({ queryKey: qk.accounts.promoAprs(id) })
  void queryClient.invalidateQueries({ queryKey: qk.accounts.detail(id) })
}

/* ================================================================== *
 * Transactions
 * ================================================================== */

export function useTransactions(
  query?: TransactionsQuery,
  options?: Options<SchemaOf<'/api/transactions'>>,
) {
  return useQuery({
    queryKey: qk.transactions.list(query),
    queryFn: ({ signal }) => unwrap(api.GET('/api/transactions', { params: { query }, signal })),
    staleTime: STALE.live,
    // Paging and filter changes keep the previous page on screen rather than
    // collapsing the table to skeletons on every keystroke.
    placeholderData: keepPreviousData,
    ...options,
  })
}

export function useTransaction(id: string, options?: Options<SchemaOf<'/api/transactions/{id}'>>) {
  return useQuery({
    queryKey: qk.transactions.detail(id),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/transactions/{id}', { params: { path: { id } }, signal })),
    staleTime: STALE.live,
    enabled: Boolean(id),
    ...options,
  })
}

export function useTransactionSubscriptions(
  id: string,
  options?: Options<SchemaOf<'/api/transactions/{id}/subscriptions'>>,
) {
  return useQuery({
    queryKey: qk.transactions.subscriptions(id),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/transactions/{id}/subscriptions', { params: { path: { id } }, signal })),
    staleTime: STALE.live,
    enabled: Boolean(id),
    ...options,
  })
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTransactionBody }) =>
      unwrap(api.PATCH('/api/transactions/{id}', { params: { path: { id } }, body })),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(qk.transactions.detail(id), data)
      void queryClient.invalidateQueries({ queryKey: qk.transactions.all })
      void queryClient.invalidateQueries({ queryKey: qk.analytics.all })
      void queryClient.invalidateQueries({ queryKey: qk.ai.reviewQueue() })
      // The dashboard counts uncategorized rows and totals month-to-date
      // spending, and both of those are exactly what this PATCH changes — a
      // category, a member, the transfer flag. It caches for STALE.balances, so
      // without this the headline "3 to categorize" sat there for five minutes
      // after the household had categorized the last one.
      void queryClient.invalidateQueries({ queryKey: qk.dashboard })
    },
  })
}

export function useBulkCategorize() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: BulkCategorizeBody) =>
      unwrap(api.POST('/api/transactions/bulk-categorize', { body })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.transactions.all })
      void queryClient.invalidateQueries({ queryKey: qk.analytics.all })
      void queryClient.invalidateQueries({ queryKey: qk.ai.all })
      // Same reason as the single-row edit, only more so: emptying the
      // uncategorized queue is the whole point of this action, and the count it
      // empties is on the dashboard.
      void queryClient.invalidateQueries({ queryKey: qk.dashboard })
    },
  })
}

/**
 * Re-runs the classification pass over transactions that already exist.
 *
 * The pass normally only sees the rows a sync just touched, so a card payment
 * whose two legs arrived on different syncs was never paired — and an unpaired
 * funding debit counts as spending forever. This is the second look.
 */
export function useReclassify() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ReclassifyBody) => unwrap(api.POST('/api/transactions/reclassify', { body })),
    onSuccess: () => {
      // Pairing a payment changes what counts as spending, so every figure derived
      // from it moves: the dashboard tiles, the analytics reports, and the rows
      // themselves, which now carry a transfer flag they did not have.
      void queryClient.invalidateQueries({ queryKey: qk.transactions.all })
      void queryClient.invalidateQueries({ queryKey: qk.analytics.all })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard })
      void queryClient.invalidateQueries({ queryKey: qk.ai.all })
    },
  })
}

export function useLinkTransactionToSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LinkSubscriptionBody }) =>
      unwrap(api.POST('/api/transactions/{id}/subscriptions', { params: { path: { id } }, body })),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: qk.transactions.detail(id) })
      void queryClient.invalidateQueries({ queryKey: qk.transactions.subscriptions(id) })
      void queryClient.invalidateQueries({ queryKey: qk.subscriptions.all })
    },
  })
}

export function useUnlinkTransactionFromSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, subscriptionId }: { id: string; subscriptionId: string }) =>
      unwrap(
        api.DELETE('/api/transactions/{id}/subscriptions/{subscriptionId}', {
          params: { path: { id, subscriptionId } },
        }),
      ),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: qk.transactions.detail(id) })
      void queryClient.invalidateQueries({ queryKey: qk.transactions.subscriptions(id) })
      void queryClient.invalidateQueries({ queryKey: qk.subscriptions.all })
    },
  })
}

/* ================================================================== *
 * Categories
 * ================================================================== */

export function useCategories(options?: Options<SchemaOf<'/api/categories'>>) {
  return useQuery({
    queryKey: qk.categories.list,
    queryFn: ({ signal }) => unwrap(api.GET('/api/categories', { signal })),
    staleTime: STALE.reference,
    ...options,
  })
}

export function useCategoryTree(options?: Options<SchemaOf<'/api/categories/tree'>>) {
  return useQuery({
    queryKey: qk.categories.tree,
    queryFn: ({ signal }) => unwrap(api.GET('/api/categories/tree', { signal })),
    staleTime: STALE.reference,
    ...options,
  })
}

export function useCreateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CategoryBody) => unwrap(api.POST('/api/categories', { body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.categories.all }),
  })
}

export function useUpdateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CategoryBody }) =>
      unwrap(api.PUT('/api/categories/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.categories.all }),
  })
}

export function useDeleteCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/categories/{id}', { params: { path: { id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.categories.all })
      void queryClient.invalidateQueries({ queryKey: qk.transactions.all })
    },
  })
}

/* ================================================================== *
 * Rules
 * ================================================================== */

export function useRules(query?: RulesQuery, options?: Options<SchemaOf<'/api/rules'>>) {
  return useQuery({
    queryKey: qk.rules.list(query),
    queryFn: ({ signal }) => unwrap(api.GET('/api/rules', { params: { query }, signal })),
    staleTime: STALE.live,
    ...options,
  })
}

export function useRule(id: string, options?: Options<SchemaOf<'/api/rules/{id}'>>) {
  return useQuery({
    queryKey: qk.rules.detail(id),
    queryFn: ({ signal }) => unwrap(api.GET('/api/rules/{id}', { params: { path: { id } }, signal })),
    staleTime: STALE.live,
    enabled: Boolean(id),
    ...options,
  })
}

export function useCreateRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: RuleBody) => unwrap(api.POST('/api/rules', { body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.rules.all }),
  })
}

export function useUpdateRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RuleBody }) =>
      unwrap(api.PUT('/api/rules/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.rules.all }),
  })
}

export function useDeleteRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/rules/{id}', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.rules.all }),
  })
}

export function useApproveRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/rules/{id}/approve', { params: { path: { id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.rules.all })
      void queryClient.invalidateQueries({ queryKey: qk.ai.all })
    },
  })
}

export function useRejectRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/rules/{id}/reject', { params: { path: { id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.rules.all })
      void queryClient.invalidateQueries({ queryKey: qk.ai.all })
    },
  })
}

/** Dry-runs a saved rule: how many past transactions would it have matched? */
export function useDryRunRule() {
  return useMutation({
    mutationFn: ({ id, months }: { id: string; months?: number }) =>
      unwrap(api.POST('/api/rules/{id}/dry-run', { params: { path: { id }, query: { months } } })),
  })
}

/** Dry-runs an unsaved draft — the preview beside the rule editor. */
export function usePreviewRule() {
  return useMutation({
    mutationFn: ({ body, months }: { body: RuleBody; months?: number }) =>
      unwrap(api.POST('/api/rules/dry-run', { params: { query: { months } }, body })),
  })
}

export function useApplyRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, months }: { id: string; months?: number }) =>
      unwrap(api.POST('/api/rules/{id}/apply', { params: { path: { id }, query: { months } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.transactions.all })
      void queryClient.invalidateQueries({ queryKey: qk.rules.all })
      void queryClient.invalidateQueries({ queryKey: qk.analytics.all })
    },
  })
}

/**
 * Rewrites rule priorities to match a new order.
 *
 * There is no batch-reorder endpoint, so this is one PUT per rule that actually
 * moved — dragging one row in a list of thirty writes two or three rules, not
 * thirty. Priorities are re-spaced in tens so a later insertion between two
 * rules does not force another rewrite.
 */
export function useReorderRules() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ordered: { id: string; priority: number | string; body: RuleBody }[]) => {
      let written = 0
      for (const [index, rule] of ordered.entries()) {
        const priority = (index + 1) * 10
        if (Number(rule.priority) === priority) continue
        await unwrap(
          api.PUT('/api/rules/{id}', {
            params: { path: { id: rule.id } },
            body: { ...rule.body, priority },
          }),
        )
        written++
      }
      return written
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.rules.all }),
  })
}

/* ================================================================== *
 * Members
 * ================================================================== */

export function useMembers(options?: Options<SchemaOf<'/api/members'>>) {
  return useQuery({
    queryKey: qk.members.list,
    queryFn: ({ signal }) => unwrap(api.GET('/api/members', { signal })),
    staleTime: STALE.reference,
    ...options,
  })
}

export function useCreateMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: MemberBody) => unwrap(api.POST('/api/members', { body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.members.all }),
  })
}

export function useUpdateMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MemberBody }) =>
      unwrap(api.PUT('/api/members/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.members.all }),
  })
}

export function useDeleteMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/members/{id}', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.members.all }),
  })
}

/* ================================================================== *
 * Income
 * ================================================================== */

export function useIncomeSources(
  query?: IncomeSourcesQuery,
  options?: Options<SchemaOf<'/api/income-sources'>>,
) {
  return useQuery({
    queryKey: qk.income.sources(query),
    queryFn: ({ signal }) => unwrap(api.GET('/api/income-sources', { params: { query }, signal })),
    staleTime: STALE.reference,
    ...options,
  })
}

export function useIncomeEvents(
  query?: IncomeEventsQuery,
  options?: Options<SchemaOf<'/api/income-events'>>,
) {
  return useQuery({
    queryKey: qk.income.events(query),
    queryFn: ({ signal }) => unwrap(api.GET('/api/income-events', { params: { query }, signal })),
    staleTime: STALE.live,
    ...options,
  })
}

export function useCreateIncomeSource() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: IncomeSourceBody) => unwrap(api.POST('/api/income-sources', { body })),
    onSuccess: () => invalidateIncome(queryClient),
  })
}

export function useUpdateIncomeSource() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: IncomeSourceBody }) =>
      unwrap(api.PUT('/api/income-sources/{id}', { params: { path: { id } }, body })),
    onSuccess: () => invalidateIncome(queryClient),
  })
}

export function useDeleteIncomeSource() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/income-sources/{id}', { params: { path: { id } } })),
    onSuccess: () => invalidateIncome(queryClient),
  })
}

export function useCreateIncomeEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: IncomeEventBody) => unwrap(api.POST('/api/income-events', { body })),
    onSuccess: () => invalidateIncome(queryClient),
  })
}

export function useUpdateIncomeEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: IncomeEventBody }) =>
      unwrap(api.PUT('/api/income-events/{id}', { params: { path: { id } }, body })),
    onSuccess: () => invalidateIncome(queryClient),
  })
}

export function useDeleteIncomeEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/income-events/{id}', { params: { path: { id } } })),
    onSuccess: () => invalidateIncome(queryClient),
  })
}

function invalidateIncome(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: qk.income.all })
  void queryClient.invalidateQueries({ queryKey: qk.analytics.all })
  void queryClient.invalidateQueries({ queryKey: qk.dashboard })
}

/* ================================================================== *
 * Subscriptions
 * ================================================================== */

export function useSubscriptions(
  query?: SubscriptionsQuery,
  options?: Options<SchemaOf<'/api/subscriptions'>>,
) {
  return useQuery({
    queryKey: qk.subscriptions.list(query),
    queryFn: ({ signal }) => unwrap(api.GET('/api/subscriptions', { params: { query }, signal })),
    staleTime: STALE.live,
    ...options,
  })
}

export function useSubscription(id: string, options?: Options<SchemaOf<'/api/subscriptions/{id}'>>) {
  return useQuery({
    queryKey: qk.subscriptions.detail(id),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/subscriptions/{id}', { params: { path: { id } }, signal })),
    staleTime: STALE.live,
    enabled: Boolean(id),
    ...options,
  })
}

export function useSubscriptionTotals(options?: Options<SchemaOf<'/api/subscriptions/totals'>>) {
  return useQuery({
    queryKey: qk.subscriptions.totals,
    queryFn: ({ signal }) => unwrap(api.GET('/api/subscriptions/totals', { signal })),
    staleTime: STALE.reports,
    ...options,
  })
}

export function useSubscriptionAllocationGaps(
  take?: number,
  options?: Options<SchemaOf<'/api/subscriptions/allocation-gaps'>>,
) {
  return useQuery({
    queryKey: qk.subscriptions.allocationGaps(take),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/subscriptions/allocation-gaps', { params: { query: { take } }, signal })),
    staleTime: STALE.live,
    ...options,
  })
}

export function useSubscriptionCharges(
  id: string,
  take?: number,
  options?: Options<SchemaOf<'/api/subscriptions/{id}/transactions'>>,
) {
  return useQuery({
    queryKey: qk.subscriptions.charges(id, take),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/subscriptions/{id}/transactions', {
          params: { path: { id }, query: { take } },
          signal,
        }),
      ),
    staleTime: STALE.live,
    enabled: Boolean(id),
    ...options,
  })
}

export function useCreateSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: SubscriptionBody) => unwrap(api.POST('/api/subscriptions', { body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.subscriptions.all }),
  })
}

export function useUpdateSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SubscriptionBody }) =>
      unwrap(api.PUT('/api/subscriptions/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.subscriptions.all }),
  })
}

export function useDeleteSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/subscriptions/{id}', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.subscriptions.all }),
  })
}

export function useLinkSubscriptionCharge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SubscriptionLinkBody }) =>
      unwrap(api.POST('/api/subscriptions/{id}/transactions', { params: { path: { id } }, body })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.subscriptions.all })
      void queryClient.invalidateQueries({ queryKey: qk.transactions.all })
    },
  })
}

export function useUnlinkSubscriptionCharge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, transactionId }: { id: string; transactionId: string }) =>
      unwrap(
        api.DELETE('/api/subscriptions/{id}/transactions/{transactionId}', {
          params: { path: { id, transactionId } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.subscriptions.all })
      void queryClient.invalidateQueries({ queryKey: qk.transactions.all })
    },
  })
}

/* ================================================================== *
 * Budgets and due-date rules
 * ================================================================== */

export function useBudgetRules(
  includeInactive?: boolean,
  options?: Options<SchemaOf<'/api/budget-rules'>>,
) {
  return useQuery({
    queryKey: qk.budgetRules.list(includeInactive),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/budget-rules', { params: { query: { includeInactive } }, signal })),
    staleTime: STALE.live,
    ...options,
  })
}

export function useCreateBudgetRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: BudgetRuleBody) => unwrap(api.POST('/api/budget-rules', { body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.budgetRules.all }),
  })
}

export function useUpdateBudgetRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: BudgetRuleBody }) =>
      unwrap(api.PUT('/api/budget-rules/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.budgetRules.all }),
  })
}

export function useDeleteBudgetRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/budget-rules/{id}', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.budgetRules.all }),
  })
}

export function useDueDateRules(options?: Options<SchemaOf<'/api/due-date-rules'>>) {
  return useQuery({
    queryKey: qk.dueDateRules.list,
    queryFn: ({ signal }) => unwrap(api.GET('/api/due-date-rules', { signal })),
    staleTime: STALE.reference,
    ...options,
  })
}

export function useCreateDueDateRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: DueDateRuleBody) => unwrap(api.POST('/api/due-date-rules', { body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.dueDateRules.all }),
  })
}

export function useUpdateDueDateRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DueDateRuleBody }) =>
      unwrap(api.PUT('/api/due-date-rules/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.dueDateRules.all }),
  })
}

export function useDeleteDueDateRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/due-date-rules/{id}', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.dueDateRules.all }),
  })
}

/* ================================================================== *
 * Alerts
 * ================================================================== */

export function useAlerts(query?: AlertsQuery, options?: Options<SchemaOf<'/api/alerts'>>) {
  return useQuery({
    queryKey: qk.alerts.list(query),
    queryFn: ({ signal }) => unwrap(api.GET('/api/alerts', { params: { query }, signal })),
    staleTime: STALE.short,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export function useAlert(id: string, options?: Options<SchemaOf<'/api/alerts/{id}'>>) {
  return useQuery({
    queryKey: qk.alerts.detail(id),
    queryFn: ({ signal }) => unwrap(api.GET('/api/alerts/{id}', { params: { path: { id } }, signal })),
    staleTime: STALE.short,
    enabled: Boolean(id),
    ...options,
  })
}

export function useAlertDeliveries(
  id: string,
  options?: Options<SchemaOf<'/api/alerts/{id}/deliveries'>>,
) {
  return useQuery({
    queryKey: qk.alerts.deliveries(id),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/alerts/{id}/deliveries', { params: { path: { id } }, signal })),
    staleTime: STALE.short,
    enabled: Boolean(id),
    ...options,
  })
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/alerts/{id}/acknowledge', { params: { path: { id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.alerts.all })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard })
    },
  })
}

/** Re-runs the alert engine now instead of waiting for the schedule. */
export function useEvaluateAlerts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (plaidItemId?: string) =>
      unwrap(api.POST('/api/alerts/evaluate', { params: { query: { plaidItemId } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.alerts.all })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard })
    },
  })
}

/* ================================================================== *
 * Analytics
 * ================================================================== */

export function useSpendingReport(
  query?: SpendingQuery,
  options?: Options<SchemaOf<'/api/analytics/spending'>>,
) {
  return useQuery({
    queryKey: qk.analytics.spending(query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/analytics/spending', { params: { query }, signal })),
    staleTime: STALE.reports,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export function useIncomeReport(
  query?: AnalyticsIncomeQuery,
  options?: Options<SchemaOf<'/api/analytics/income'>>,
) {
  return useQuery({
    queryKey: qk.analytics.income(query),
    queryFn: ({ signal }) => unwrap(api.GET('/api/analytics/income', { params: { query }, signal })),
    staleTime: STALE.reports,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export function useNetWorthSeries(
  query?: NetWorthQuery,
  options?: Options<SchemaOf<'/api/analytics/net-worth'>>,
) {
  return useQuery({
    queryKey: qk.analytics.netWorth(query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/analytics/net-worth', { params: { query }, signal })),
    staleTime: STALE.reports,
    ...options,
  })
}

export function useCashflow(months?: number, options?: Options<SchemaOf<'/api/analytics/cashflow'>>) {
  return useQuery({
    queryKey: qk.analytics.cashflow(months),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/analytics/cashflow', { params: { query: { months } }, signal })),
    staleTime: STALE.reports,
    ...options,
  })
}

export function useSubscriptionAnalytics(
  options?: Options<SchemaOf<'/api/analytics/subscriptions'>>,
) {
  return useQuery({
    queryKey: qk.analytics.subscriptions,
    queryFn: ({ signal }) => unwrap(api.GET('/api/analytics/subscriptions', { signal })),
    staleTime: STALE.reports,
    ...options,
  })
}

/* ================================================================== *
 * Investments
 * ================================================================== */

export function useHoldings(
  query?: PortfolioScopeQuery,
  options?: Options<SchemaOf<'/api/investments/holdings'>>,
) {
  return useQuery({
    queryKey: qk.investments.holdings(query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/investments/holdings', { params: { query }, signal })),
    staleTime: STALE.balances,
    ...options,
  })
}

export function useSecurities(options?: Options<SchemaOf<'/api/investments/securities'>>) {
  return useQuery({
    queryKey: qk.investments.securities,
    queryFn: ({ signal }) => unwrap(api.GET('/api/investments/securities', { signal })),
    staleTime: STALE.reference,
    ...options,
  })
}

export function useInvestmentTransactions(
  query?: InvestmentTransactionsQuery,
  options?: Options<SchemaOf<'/api/investments/transactions'>>,
) {
  return useQuery({
    queryKey: qk.investments.transactions(query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/investments/transactions', { params: { query }, signal })),
    staleTime: STALE.balances,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export function useAllocation(
  query?: PortfolioScopeQuery,
  options?: Options<SchemaOf<'/api/investments/allocation'>>,
) {
  return useQuery({
    queryKey: qk.investments.allocation(query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/investments/allocation', { params: { query }, signal })),
    staleTime: STALE.balances,
    ...options,
  })
}

/**
 * Unrealized gain against cost basis, plus realized gain reconstructed FIFO from
 * investment transactions. Both carry the coverage they were computed over — read it
 * before quoting the headline.
 */
export function usePortfolioPerformance(
  query?: PortfolioPerformanceQuery,
  options?: Options<SchemaOf<'/api/investments/performance'>>,
) {
  return useQuery({
    queryKey: qk.investments.performance(query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/investments/performance', { params: { query }, signal })),
    staleTime: STALE.balances,
    ...options,
  })
}

/** Portfolio value per day, from holding snapshots rather than account balances. */
export function usePortfolioValueSeries(
  query?: PortfolioValueSeriesQuery,
  options?: Options<SchemaOf<'/api/investments/value-series'>>,
) {
  return useQuery({
    queryKey: qk.investments.valueSeries(query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/investments/value-series', { params: { query }, signal })),
    staleTime: STALE.balances,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export function usePortfolioMovers(
  query?: PortfolioMoversQuery,
  options?: Options<SchemaOf<'/api/investments/movers'>>,
) {
  return useQuery({
    queryKey: qk.investments.movers(query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/investments/movers', { params: { query }, signal })),
    staleTime: STALE.balances,
    placeholderData: keepPreviousData,
    ...options,
  })
}

export function usePortfolioConcentration(
  query?: PortfolioConcentrationQuery,
  options?: Options<SchemaOf<'/api/investments/concentration'>>,
) {
  return useQuery({
    queryKey: qk.investments.concentration(query),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/investments/concentration', { params: { query }, signal })),
    staleTime: STALE.balances,
    ...options,
  })
}

/* ================================================================== *
 * Debt
 * ================================================================== */

export function useDebtSummary(options?: Options<SchemaOf<'/api/debt/summary'>>) {
  return useQuery({
    queryKey: qk.debt.summary,
    queryFn: ({ signal }) => unwrap(api.GET('/api/debt/summary', { signal })),
    staleTime: STALE.balances,
    ...options,
  })
}

export function usePayoffPlan(
  accountId: string,
  options?: Options<SchemaOf<'/api/debt/cards/{accountId}/payoff'>>,
) {
  return useQuery({
    queryKey: qk.debt.payoff(accountId),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/debt/cards/{accountId}/payoff', { params: { path: { accountId } }, signal }),
      ),
    staleTime: STALE.balances,
    enabled: Boolean(accountId),
    ...options,
  })
}

/**
 * A loan's schedule, optionally with an overpayment modelled alongside it.
 *
 * `extraMonthlyPayment` is part of the query key, so dragging the control
 * between $100 and $200 keeps both answers cached and switching back is
 * instant rather than a refetch.
 */
export function useAmortization(
  accountId: string,
  extraMonthlyPayment = 0,
  options?: Options<SchemaOf<'/api/debt/loans/{accountId}/amortization'>>,
) {
  return useQuery({
    queryKey: qk.debt.amortization(accountId, extraMonthlyPayment),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/debt/loans/{accountId}/amortization', {
          params: {
            path: { accountId },
            query: extraMonthlyPayment > 0 ? { extraMonthlyPayment } : {},
          },
          signal,
        }),
      ),
    staleTime: STALE.reports,
    enabled: Boolean(accountId),
    ...options,
  })
}

/**
 * Avalanche against snowball on a monthly budget.
 *
 * `monthlyBudget` null asks the server for its own default — the household's
 * frozen planned payments (D20), floored at the issuer minimums (D21) — which
 * is the honest starting point before anyone starts moving a slider.
 */
export function usePayoffStrategy(
  monthlyBudget: number | null,
  options?: Options<SchemaOf<'/api/debt/strategy'>>,
) {
  return useQuery({
    queryKey: qk.debt.strategy(monthlyBudget),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/debt/strategy', {
          params: { query: monthlyBudget === null ? {} : { monthlyBudget } },
          signal,
        }),
      ),
    staleTime: STALE.reports,
    placeholderData: keepPreviousData,
    ...options,
  })
}

/** What every promotional balance costs once its window shuts (D22). */
export function usePromoCost(options?: Options<SchemaOf<'/api/debt/promo-cost'>>) {
  return useQuery({
    queryKey: qk.debt.promoCost,
    queryFn: ({ signal }) => unwrap(api.GET('/api/debt/promo-cost', { signal })),
    staleTime: STALE.reports,
    ...options,
  })
}

/* ================================================================== *
 * AI
 * ================================================================== */

export function useAiStatus(options?: Options<SchemaOf<'/api/ai/status'>>) {
  return useQuery({
    queryKey: qk.ai.status,
    queryFn: ({ signal }) => unwrap(api.GET('/api/ai/status', { signal })),
    staleTime: STALE.short,
    ...options,
  })
}

export function useConversations(options?: Options<SchemaOf<'/api/ai/conversations'>>) {
  return useQuery({
    queryKey: qk.ai.conversations,
    queryFn: ({ signal }) => unwrap(api.GET('/api/ai/conversations', { signal })),
    staleTime: STALE.live,
    ...options,
  })
}

export function useConversation(
  id: string,
  options?: Options<SchemaOf<'/api/ai/conversations/{id}'>>,
) {
  return useQuery({
    queryKey: qk.ai.conversation(id),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/ai/conversations/{id}', { params: { path: { id } }, signal })),
    staleTime: STALE.live,
    enabled: Boolean(id),
    ...options,
  })
}

/**
 * Loads one conversation on demand and seeds the cache with it.
 *
 * The chat panel needs this because the SSE stream carries prose only: the tool
 * calls behind an answer are persisted server-side and arrive with the
 * conversation, so the panel fetches it the moment a stream finishes rather
 * than waiting for a stale time to lapse.
 */
export function useLoadConversation() {
  const queryClient = useQueryClient()
  return useCallback(
    (id: string) =>
      queryClient.fetchQuery({
        queryKey: qk.ai.conversation(id),
        queryFn: ({ signal }) =>
          unwrap(api.GET('/api/ai/conversations/{id}', { params: { path: { id } }, signal })),
        staleTime: 0,
      }),
    [queryClient],
  )
}

export function useDeleteConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/ai/conversations/{id}', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.ai.conversations }),
  })
}

export function useInsights(query?: InsightsQuery, options?: Options<SchemaOf<'/api/ai/insights'>>) {
  return useQuery({
    queryKey: qk.ai.insights(query),
    queryFn: ({ signal }) => unwrap(api.GET('/api/ai/insights', { params: { query }, signal })),
    staleTime: STALE.short,
    ...options,
  })
}

export function useDismissInsight() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/ai/insights/{id}/dismiss', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.ai.all }),
  })
}

/** Low-confidence AI categorizations awaiting a human yes/no. */
export function useReviewQueue(take?: number, options?: Options<SchemaOf<'/api/ai/review-queue'>>) {
  return useQuery({
    queryKey: qk.ai.reviewQueue(take),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/ai/review-queue', { params: { query: { take } }, signal })),
    staleTime: STALE.live,
    ...options,
  })
}

export function useApproveReviewItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ApproveCategoryBody) =>
      unwrap(api.POST('/api/ai/review-queue/approve', { body })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.ai.all })
      void queryClient.invalidateQueries({ queryKey: qk.transactions.all })
    },
  })
}

/* ================================================================== *
 * Settings
 * ================================================================== */

export function useSettings(options?: Options<SchemaOf<'/api/settings'>>) {
  return useQuery({
    queryKey: qk.settings.detail,
    queryFn: ({ signal }) => unwrap(api.GET('/api/settings', { signal })),
    staleTime: STALE.reference,
    ...options,
  })
}

export function useUpdateSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: SettingsBody) => unwrap(api.PUT('/api/settings', { body })),
    onSuccess: (data) => {
      queryClient.setQueryData(qk.settings.detail, data)
      // Payoff strategy and the mortgage toggle change every debt figure.
      void queryClient.invalidateQueries({ queryKey: qk.debt.all })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard })
    },
  })
}

export function useRecipients(options?: Options<SchemaOf<'/api/settings/recipients'>>) {
  return useQuery({
    queryKey: qk.settings.recipients,
    queryFn: ({ signal }) => unwrap(api.GET('/api/settings/recipients', { signal })),
    staleTime: STALE.reference,
    ...options,
  })
}

export function useCreateRecipient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: RecipientBody) => unwrap(api.POST('/api/settings/recipients', { body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.settings.recipients }),
  })
}

export function useUpdateRecipient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RecipientBody }) =>
      unwrap(api.PUT('/api/settings/recipients/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.settings.recipients }),
  })
}

/**
 * What the channel said when we poked it.
 *
 * Hand-written for the reason `SessionResponse` is: ASP.NET does not describe
 * this route's 200 body, so the generated contract types it as `never`.
 *
 * `delivered: false` arrives with a 200 — the request succeeded, the delivery
 * did not — so a caller that only watches for a thrown error reports a bad
 * webhook URL as a success. Read `delivered`.
 */
export interface TestNotificationResult {
  delivered: boolean
  channel: string
  detail: string
}

export function useTestRecipient() {
  return useMutation({
    mutationFn: async (id: string) =>
      (await unwrap(
        api.POST('/api/settings/recipients/{id}/test', { params: { path: { id } } }),
      )) as unknown as TestNotificationResult,
    // Nothing to invalidate, unlike every other mutation here: the probe alert is
    // built in memory and handed straight to the channel, never saved, so no
    // recipient row and no delivery row moves.
  })
}

export function useDeleteRecipient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/settings/recipients/{id}', { params: { path: { id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.settings.recipients })
      // A removed recipient is dropped from every watch that aimed at it, so a
      // cached watch list would still be naming an address nothing can reach.
      void queryClient.invalidateQueries({ queryKey: qk.transactionWatches.all })
    },
  })
}

/* ================================================================== *
 * Transaction watches
 * ================================================================== */

export function useTransactionWatches(options?: Options<SchemaOf<'/api/transaction-watches'>>) {
  return useQuery({
    queryKey: qk.transactionWatches.list,
    queryFn: ({ signal }) => unwrap(api.GET('/api/transaction-watches', { signal })),
    staleTime: STALE.reference,
    ...options,
  })
}

export function useCreateTransactionWatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: TransactionWatchBody) =>
      unwrap(api.POST('/api/transaction-watches', { body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.transactionWatches.all }),
  })
}

export function useUpdateTransactionWatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TransactionWatchBody }) =>
      unwrap(api.PUT('/api/transaction-watches/{id}', { params: { path: { id } }, body })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.transactionWatches.all }),
  })
}

export function useDeleteTransactionWatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/transaction-watches/{id}', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.transactionWatches.all }),
  })
}

/* ================================================================== *
 * Plaid connections
 * ================================================================== */

export function usePlaidItems(options?: Options<SchemaOf<'/api/plaid/items'>>) {
  return useQuery({
    queryKey: qk.plaid.items,
    queryFn: ({ signal }) => unwrap(api.GET('/api/plaid/items', { signal })),
    staleTime: STALE.short,
    ...options,
  })
}

/** Link tokens are single-use and short-lived, so this is a mutation. */
export function useCreateLinkToken() {
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/plaid/link-token', {})),
  })
}

/** Update mode: re-authenticating an item that needs a fresh sign-in. */
export function useCreateUpdateLinkToken() {
  return useMutation({
    mutationFn: (itemId: string) =>
      unwrap(api.POST('/api/plaid/link-token/update/{itemId}', { params: { path: { itemId } } })),
  })
}

export function useExchangePublicToken() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ExchangeBody) => unwrap(api.POST('/api/plaid/exchange', { body })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.plaid.all })
      void queryClient.invalidateQueries({ queryKey: qk.accounts.all })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard })
    },
  })
}

export function useSyncItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, type }: { id: string; type?: string }) =>
      unwrap(api.POST('/api/plaid/items/{id}/sync', { params: { path: { id }, query: { type } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.plaid.all }),
  })
}

export function useDeletePlaidItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/plaid/items/{id}', { params: { path: { id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.plaid.all })
      void queryClient.invalidateQueries({ queryKey: qk.accounts.all })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard })
    },
  })
}

/* ================================================================== *
 * Card payment plans (Amex Plan It, Chase My Chase Plan, Citi Flex Pay)
 *
 * Appended rather than filed beside the account hooks above: this file
 * is edited by more than one pair of hands at a time, and appending is
 * the only edit that never collides.
 *
 * Every mutation invalidates the debt screens as well as the account.
 * A plan changes what a statement balance decomposes into, which moves
 * the payoff figure, the shortfall and "due this month" — leaving those
 * stale would show two different answers on two tabs.
 * ================================================================== */

export type PaymentPlanBody = Body<'/api/accounts/{accountId}/payment-plans', 'post'>
export type AcceptPlanSuggestionBody = Body<
  '/api/statements/plan-suggestions/{id}/accept',
  'post'
>

export const paymentPlanKeys = {
  list: (accountId: string) => ['accounts', accountId, 'payment-plans'] as const,
  suggestions: (accountId: string) => ['accounts', accountId, 'plan-suggestions'] as const,
}

export function usePaymentPlans(
  accountId: string,
  options?: Options<SchemaOf<'/api/accounts/{accountId}/payment-plans'>>,
) {
  return useQuery({
    queryKey: paymentPlanKeys.list(accountId),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/accounts/{accountId}/payment-plans', {
          params: { path: { accountId } },
          signal,
        }),
      ),
    staleTime: STALE.live,
    enabled: Boolean(accountId),
    ...options,
  })
}

export function usePlanSuggestions(
  accountId: string,
  options?: Options<SchemaOf<'/api/accounts/{accountId}/statements/plan-suggestions'>>,
) {
  return useQuery({
    queryKey: paymentPlanKeys.suggestions(accountId),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/accounts/{accountId}/statements/plan-suggestions', {
          params: { path: { accountId } },
          signal,
        }),
      ),
    staleTime: STALE.short,
    enabled: Boolean(accountId),
    ...options,
  })
}

export function useCreatePaymentPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, body }: { accountId: string; body: PaymentPlanBody }) =>
      unwrap(
        api.POST('/api/accounts/{accountId}/payment-plans', {
          params: { path: { accountId } },
          body,
        }),
      ),
    onSuccess: (_data, { accountId }) => invalidatePaymentPlans(queryClient, accountId),
  })
}

export function useUpdatePaymentPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      accountId,
      planId,
      body,
    }: {
      accountId: string
      planId: string
      body: PaymentPlanBody
    }) =>
      unwrap(
        api.PUT('/api/accounts/{accountId}/payment-plans/{planId}', {
          params: { path: { accountId, planId } },
          body,
        }),
      ),
    onSuccess: (_data, { accountId }) => invalidatePaymentPlans(queryClient, accountId),
  })
}

export function useDeletePaymentPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, planId }: { accountId: string; planId: string }) =>
      unwrap(
        api.DELETE('/api/accounts/{accountId}/payment-plans/{planId}', {
          params: { path: { accountId, planId } },
        }),
      ),
    onSuccess: (_data, { accountId }) => invalidatePaymentPlans(queryClient, accountId),
  })
}

export function useAcceptPlanSuggestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      accountId: _accountId,
      id,
      body,
    }: {
      accountId: string
      id: string
      body?: AcceptPlanSuggestionBody
    }) =>
      unwrap(
        api.POST('/api/statements/plan-suggestions/{id}/accept', {
          params: { path: { id } },
          body: body ?? {},
        }),
      ),
    onSuccess: (_data, { accountId }) => invalidatePaymentPlans(queryClient, accountId),
  })
}

export function useDismissPlanSuggestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId: _accountId, id }: { accountId: string; id: string }) =>
      unwrap(
        api.POST('/api/statements/plan-suggestions/{id}/dismiss', { params: { path: { id } } }),
      ),
    onSuccess: (_data, { accountId }) =>
      void queryClient.invalidateQueries({ queryKey: paymentPlanKeys.suggestions(accountId) }),
  })
}

function invalidatePaymentPlans(queryClient: QueryClient, accountId: string) {
  void queryClient.invalidateQueries({ queryKey: paymentPlanKeys.list(accountId) })
  void queryClient.invalidateQueries({ queryKey: paymentPlanKeys.suggestions(accountId) })
  void queryClient.invalidateQueries({ queryKey: qk.accounts.detail(accountId) })
  void queryClient.invalidateQueries({ queryKey: qk.debt.all })
  void queryClient.invalidateQueries({ queryKey: qk.dashboard })
}

/* ================================================================== *
 * The statement archive
 *
 * Appended for the same reason the block above is: this file is edited
 * by more than one pair of hands, and appending never collides.
 *
 * Every mutation here spends money at a vendor, and all four are Owner
 * only on the server. `usePermissions().canAdminister` decides whether
 * the control is offered at all — hiding it is a courtesy, the policy is
 * the boundary.
 *
 * They all invalidate through one helper. A backfill downloads and reads
 * statements, which appends statement cycles and can raise installment
 * plan suggestions, so leaving the account's cycles or its suggestions
 * cached would show two different answers on two panels of the same page.
 * ================================================================== */

export const statementKeys = {
  list: (accountId: string) => ['accounts', accountId, 'statements'] as const,
  /**
   * Nested under the list on purpose: invalidating the list invalidates the
   * opt-in state with it, and the two are never meaningfully out of step —
   * turning the import off is exactly when the list stops being able to grow.
   */
  settings: (accountId: string) => ['accounts', accountId, 'statements', 'settings'] as const,
}

/** Body of `PUT .../statements/settings`. */
export type StatementImportSettingBody = Body<
  '/api/accounts/{accountId}/statements/settings',
  'put'
>

/** Body of `POST .../statements/backfill`. Optional — the server has a default ceiling. */
export type StatementBackfillBody = Body<'/api/accounts/{accountId}/statements/backfill', 'post'>

export function useStatements(
  accountId: string,
  options?: Options<SchemaOf<'/api/accounts/{accountId}/statements'>>,
) {
  return useQuery({
    queryKey: statementKeys.list(accountId),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/accounts/{accountId}/statements', {
          params: { path: { accountId } },
          signal,
        }),
      ),
    staleTime: STALE.balances,
    enabled: Boolean(accountId),
    ...options,
  })
}

/**
 * Whether this account is opted in, and what the archive already holds.
 *
 * Separate from the list because the two answer different questions: the list
 * says what is known, this says whether anything is allowed to become known.
 * An account with an empty list and the import off is a completely different
 * screen from one with an empty list and the import on.
 */
export function useStatementSettings(
  accountId: string,
  options?: Options<SchemaOf<'/api/accounts/{accountId}/statements/settings'>>,
) {
  return useQuery({
    queryKey: statementKeys.settings(accountId),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/accounts/{accountId}/statements/settings', {
          params: { path: { accountId } },
          signal,
        }),
      ),
    staleTime: STALE.balances,
    enabled: Boolean(accountId),
    ...options,
  })
}

/** Opt this account in or out. Owner only on the server; billable once on. */
export function useUpdateStatementImport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, body }: { accountId: string; body: StatementImportSettingBody }) =>
      unwrap(
        api.PUT('/api/accounts/{accountId}/statements/settings', {
          params: { path: { accountId } },
          body,
        }),
      ),
    onSuccess: (_data, { accountId }) => invalidateStatements(queryClient, accountId),
  })
}

/**
 * Queue the download-and-read pass: fetch the statements Plaid lists but we do
 * not hold, then read the ones that have never been read or that failed.
 * Billed by Plaid per PDF and by OpenRouter per read.
 */
export function useBackfillStatements() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, body }: { accountId: string; body?: StatementBackfillBody }) =>
      unwrap(
        api.POST('/api/accounts/{accountId}/statements/backfill', {
          params: { path: { accountId } },
          body: body ?? {},
        }),
      ),
    onSuccess: (_data, { accountId }) => invalidateStatements(queryClient, accountId),
  })
}

/**
 * Ask Plaid whether the institution has published anything new. The answer
 * arrives by webhook rather than in this response, so the list is invalidated
 * for the newly discovered rows rather than for a result this call carries.
 */
export function useRefreshStatements() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) =>
      unwrap(
        api.POST('/api/accounts/{accountId}/statements/refresh', {
          params: { path: { accountId } },
        }),
      ),
    onSuccess: (_data, { accountId }) => invalidateStatements(queryClient, accountId),
  })
}

/**
 * Re-read every statement already held, ignoring what an earlier extraction
 * concluded. The expensive one: it downloads nothing, and pays to read the
 * whole archive again. The response reports how many statements that was, so
 * the screen can say what it just spent rather than only that it started.
 */
export function useReExtractStatements() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) =>
      unwrap(
        api.POST('/api/accounts/{accountId}/statements/re-extract', {
          params: { path: { accountId } },
        }),
      ),
    onSuccess: (_data, { accountId }) => invalidateStatements(queryClient, accountId),
  })
}

function invalidateStatements(queryClient: QueryClient, accountId: string) {
  void queryClient.invalidateQueries({ queryKey: statementKeys.list(accountId) })
  void queryClient.invalidateQueries({ queryKey: paymentPlanKeys.suggestions(accountId) })
  void queryClient.invalidateQueries({ queryKey: qk.accounts.detail(accountId) })

  // The cycles key carries a `take`, and every page size holds a different
  // cached list — so the prefix is invalidated rather than one arbitrary size.
  void queryClient.invalidateQueries({ queryKey: ['accounts', accountId, 'cycles'] })

  // A queued job is what the sync panel reports on, exactly as it is after a
  // manual Plaid sync.
  void queryClient.invalidateQueries({ queryKey: qk.plaid.all })
}

/**
 * Where the stored PDF lives.
 *
 * A URL rather than a hook because this is for an `<a href target="_blank">`:
 * the API authenticates with the `pf_session` cookie and the app is served
 * from the same origin, so the browser sends it on a plain navigation. Fetching
 * the bytes into a blob to hand them back to the same browser would buy
 * nothing and would break the PDF viewer's own paging.
 */
export function statementContentUrl(accountId: string, statementId: string): string {
  return `${API_BASE_URL}/api/accounts/${accountId}/statements/${statementId}/content`
}

/* ================================================================== *
 * Root's user administration
 *
 * Appended for the same reason the two blocks above are.
 *
 * Root holds no household role, so `/api/members` — the roster every
 * other screen reads — refuses it. This is the same reference data
 * reached through the one route group root is allowed into, and it is
 * the only household data root can see at all.
 * ================================================================== */

export const adminKeys = {
  /** Deliberately not under `['admin', 'users']`: a user edit invalidates that
   *  prefix, and the member roster does not change when a login does. */
  members: ['admin', 'members'] as const,
}

/**
 * A household member as root may see them — a name and an id, and nothing
 * about the money. Hand-written for the reason `SessionResponse` is: ASP.NET
 * does not describe the admin group's 200 bodies, so the generated contract
 * types them as `never`.
 */
export interface AdminMemberOption {
  id: string
  displayName: string
}

export function useAdminMembers(options?: Options<AdminMemberOption[]>) {
  return useQuery({
    queryKey: adminKeys.members,
    queryFn: async ({ signal }) =>
      (await unwrap(
        api.GET('/api/admin/users/members', { signal }),
      )) as unknown as AdminMemberOption[],
    staleTime: STALE.reference,
    ...options,
  })
}
